import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { JoonUIColor, Typography } from "@joonapp/web-shared"
import { useEffect, useMemo } from "react"

import { useAddFamilyMemberModalStore } from "./hooks/useAddFamilyMemberModalStore"
import useFamilyUserSelectHeaderStore from "./hooks/useFamilyUserSelect"
import UserSelectHeader from "../../components/userSelectHeader/UserSelectHeader"
import useMobile from "../../hooks/useMobile"
import { useFlaggedDoterChatLogsQuery } from "../../networking/chatSessions/queries"
import {
  useFamilyMailQuery,
  useFamilyQuery,
  useUserQuery,
} from "../../networking/queries"
import { FamilyQuerySelect, FlagType, UserSelectFilterType } from "../../types"

const FamilyUserSelectHeader = () => {
  const { selectedUserId, selectedFilterType, setSelectedUserId } =
    useFamilyUserSelectHeaderStore()
  const { onOpen: openAddFamilyMemberModal } = useAddFamilyMemberModalStore()
  const { user } = useUserQuery()
  const { data: unreadMail } = useFamilyMailQuery({ isUnread: true })

  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { data: redFlaggedConvos } = useFlaggedDoterChatLogsQuery(FlagType.RED)
  const { data: greenFlaggedConvos } = useFlaggedDoterChatLogsQuery(
    FlagType.GREEN
  )
  const isMobile = useMobile()

  const pausedUserIds = useMemo(() => {
    const ids = [] as number[]
    children?.forEach(
      (child) => child.user.active_doter.is_paused && ids.push(child.user.id)
    )
    return ids
  }, [children])

  const notificationUserIds = useMemo(() => {
    const idMap = {} as Record<number, boolean>

    // notifications for unread mail
    unreadMail?.forEach((mail) => {
      if (!user) return
      if (mail.sender.id === user.id) return
      if (mail.read_datetime) return
      if (idMap[user.id]) return
      idMap[user.id] = true
    })

    // notifications for flagged convos
    const convos = [...(redFlaggedConvos || []), ...(greenFlaggedConvos || [])]
    convos?.forEach((convo) => {
      if (convo.date_read) return
      if (idMap[convo.user_id]) return
      idMap[convo.user_id] = true
    })

    // notifications for non onboarded kids
    children?.forEach((child) => {
      if (child.user.active_doter.date_hatched) return
      if (idMap[child.user.id]) return
      idMap[child.user.id] = true
    })

    return Object.keys(idMap).map(Number)
  }, [unreadMail, redFlaggedConvos, greenFlaggedConvos, user, children])

  const onUserSelect = (
    _filterType: UserSelectFilterType,
    userId: number | null
  ) => {
    setSelectedUserId(userId)
  }

  // Set current user by default if no user is selected
  useEffect(() => {
    if (!!selectedUserId || !user) return
    setSelectedUserId(user?.id)
  }, [selectedUserId, user, setSelectedUserId])

  const handleAddUser = () => {
    openAddFamilyMemberModal()
  }

  return (
    <div
      className={`pt-3 pb-2 w-full flex flex-row items-center justify-between max-w-[600px] mx-auto sticky top-0 ${
        isMobile ? "bg-white" : "bg-bgXLightGray"
      } z-20`}
    >
      <UserSelectHeader
        selectedUserId={selectedUserId}
        onUserSelect={onUserSelect}
        selectedFilterType={selectedFilterType}
        showAllOption={false}
        showAllKidsOption={false}
        pausedUserIds={pausedUserIds}
        notificationUserIds={notificationUserIds}
      />

      <button
        className="rounded-bl-md rounded-tl-md overflow-hidden"
        onClick={handleAddUser}
      >
        <div className="flex flex-col items-center justify-center gap-1 bg-indigo-100 p-2">
          <FontAwesomeIcon
            icon={faPlus}
            color={JoonUIColor.text.primaryAccent}
          />
          <Typography
            variant="bodySmallBold"
            textAlign="center"
            color={JoonUIColor.text.primaryAccent}
          >
            Add
          </Typography>
        </div>
      </button>
    </div>
  )
}

export default FamilyUserSelectHeader
